import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, Box, TextField } from "@mui/material";
import { type HTMLAttributes, type Ref, useState } from "react";
import {
	type Control,
	Controller,
	type FieldValues,
	type Path,
} from "react-hook-form";

import type { Heat } from "@/shared/models/Heat";
import { useInspectionHeatsQuery } from "../../hooks/queries/useInspectionHeatsQuery";

interface Props<T extends FieldValues> {
	control: Control<T>;
	assetId: number;
	sectionId: number | null;
	inputRef?: Ref<HTMLInputElement>;
}

const heatLabel = (heat: Heat) => `${heat.HeatName}`;

const renderInspectionHeatOption = (
	props: HTMLAttributes<HTMLLIElement>,
	option: Heat,
) => (
	<Box
		{...props}
		component="li"
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "flex-start !important",
			width: "100%",
		}}
	>
		<Box sx={{ width: "100%" }}>{heatLabel(option)}</Box>
		<Box sx={{ fontSize: "0.8em", color: "grey", width: "100%" }}>
			{`${option.GradeName ? `Grade: ${option.GradeName}` : ""}${
				option.Sections ? ` | Section: ${option.Sections}` : ""
			}`}
		</Box>
	</Box>
);

export const InspectionHeatField = <T extends FieldValues>({
	control,
	assetId,
	sectionId,
	inputRef,
}: Props<T>) => {
	const [error, setError] = useState("");
	const heatsQuery = useInspectionHeatsQuery({
		assetId,
		sectionId,
		onError: (error) => setError(error.message),
	});
	return (
		<>
			<Controller
				name={"heatId" as Path<T>}
				control={control}
				rules={{ required: "Heat number is required" }}
				render={({ field, fieldState }) => (
					<Autocomplete
						size="small"
						autoHighlight
						clearOnEscape
						disabled={sectionId === null}
						sx={{ width: 260, maxWidth: "100%" }}
						options={heatsQuery.data ?? []}
						loading={heatsQuery.isLoading}
						value={
							heatsQuery.data?.find((heat) => heat.HeatId === field.value) ??
							null
						}
						getOptionLabel={heatLabel}
						isOptionEqualToValue={(option, value) =>
							option.HeatId === value.HeatId
						}
						onChange={(_, value) => field.onChange(value?.HeatId ?? null)}
						onBlur={field.onBlur}
						renderOption={renderInspectionHeatOption}
						renderInput={(params) => (
							<TextField
								{...params}
								inputProps={{
									...params.inputProps,
									"data-test-id": "inspection-heat-input",
								}}
								name={field.name}
								inputRef={inputRef ?? field.ref}
								label="Heat"
								margin="none"
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
							/>
						)}
					/>
				)}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
